import { useEffect, useState } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export function AttachPageDialog({ invoice, open, onClose, onAttached }) {
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !invoice) return;
    const t = setTimeout(() => {
      setLoading(true);
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      api.get(`/invoices/${invoice.id}/page-candidates${params}`)
        .then((r) => setCands(r.data))
        .catch(() => setCands([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [open, invoice, q]);

  useEffect(() => { if (open) setQ(""); }, [open]);

  const attach = async (cand) => {
    setBusy(cand.id);
    try {
      await api.post(`/invoices/${invoice.id}/attach-page`, { page_id: cand.id });
      toast.success(`Página asociada: ${cand.file_name}`);
      onAttached();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al asociar la página");
    } finally {
      setBusy(null);
    }
  };

  const shown = cands;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="attach-page-dialog" aria-describedby="attach-page-desc">
        <DialogHeader>
          <DialogTitle className="font-heading">Asociar página a {invoice?.invoice_number || invoice?.file_name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500" id="attach-page-desc">
          La factura elegida pasará a ser la página 2 de este documento: dejará de contar como gasto independiente
          y se conservará íntegra para trazabilidad. Candidatas ordenadas por proveedor/fecha coincidente.
        </p>
        <Input data-testid="attach-page-search" placeholder="Buscar por proveedor, nº o archivo..." value={q}
          onChange={(e) => setQ(e.target.value)} className="h-9" />
        {loading && <div className="py-10 text-center text-sm text-slate-400">Buscando candidatas...</div>}
        {!loading && shown.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-400">No hay facturas candidatas.</div>
        )}
        <div className="space-y-2">
          {shown.map((c) => (
            <div key={c.id} data-testid={`attach-candidate-${c.id}`}
              className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" title={c.file_name}>{c.file_name}</div>
                <div className="text-xs text-slate-500">
                  {c.supplier_name || "Sin proveedor"} · {c.invoice_number || "s/n"} · {fmtDate(c.invoice_date)} · <span className="font-num">{fmtEUR(c.total)}</span>
                </div>
              </div>
              {c.has_payments ? (
                <span className="text-xs text-amber-700 shrink-0" data-testid={`attach-blocked-${c.id}`}>Tiene pagos — no asociable</span>
              ) : (
                <Button size="sm" data-testid={`attach-page-${c.id}`} disabled={busy === c.id}
                  onClick={() => attach(c)} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white shrink-0">
                  {busy === c.id ? "Asociando..." : "Asociar"}
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
